import type { LucideIcon } from 'lucide-react';
import { BuildingEntityIcon, EntityIcon } from '@hqo/entity-icon';

import type { ActivityItem } from 'app/data/types/activity';

interface ActivityTileProps {
  item: ActivityItem;
  /** The per-type glyph, shown whenever there is no picture to show instead. */
  Icon: LucideIcon;
}

/** The enlarged treatment (56px). `md` is the 40px tile this replaced. */
const SIZE = 'lg';

/**
 * Two letters from a person's name, or nothing.
 *
 * Derived here rather than in the tile because only this app knows that a visitor row's `title` IS
 * the person — for every other type it is a room, an event or a work-order label, and initialling
 * those would put "OH" over a photo of Oak Hall.
 *
 * First and last, so "Sarah Chen" is SC and "Maria de la Cruz" is MC rather than MD. Non-letters
 * are dropped so a name like "Jean-Luc" cannot contribute a hyphen.
 *
 * Indexed by CODE POINT, not by `[0]`. The filter above is Unicode-aware and so keeps letters
 * outside the BMP, and `[0]` on one of those returns half a surrogate pair — a replacement glyph
 * rather than the letter. Not hypothetical: 𠮷 (U+20BB7) is an ordinary Japanese surname character.
 */
const initialsOf = (name?: string): string | undefined => {
  const words = (name ?? '')
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}]/gu, ''))
    .filter(Boolean);

  if (!words.length) {
    return undefined;
  }

  const [first] = Array.from(words[0]);
  const [last = ''] = words.length > 1 ? Array.from(words[words.length - 1]) : [];

  return `${first}${last}`.toUpperCase();
};

/**
 * The picture at the head of a card.
 *
 * Which picture depends on what the row is about, and the three cases are genuinely different
 * records rather than one with variations:
 *
 * - **event / resource booking** — its own photo, off the projection. No building lookup: the
 *   record has a picture of itself, and a room's photo is the point of the tile.
 * - **visitor** — the visitor's initials over the building's photo. The person has no picture, so
 *   the building places them and the initials say who.
 * - **work order** — the building's photo. Same reasoning, minus a person to name.
 *
 * Every case degrades to the per-type glyph: no image, a failed load, or a building we cannot
 * resolve all land on the same fallback rather than an empty grey square.
 */
export const ActivityTile = ({ item, Icon }: ActivityTileProps): JSX.Element => {
  const { type, details, context } = item;
  // 29px in a 56px tile keeps the glyph at the same optical weight it had at 21px in the old 40px
  // one. Left at 24 it reads noticeably smaller than before, which is a regression dressed up as a
  // resize — the fallback should look like it always did, just larger.
  const fallback = <Icon size={29} strokeWidth={1.75} />;

  // The record's own photo. `imageUrl` may be a stored relative path or an absolute url depending
  // on the vertical — the tile resolves either, and sizes it to what it actually draws.
  if (type === 'event' || type === 'resource_booking') {
    return <EntityIcon size={SIZE} imageUrl={details.imageUrl} fallback={fallback} alt="" />;
  }

  const text = type === 'visitor_registration' ? initialsOf(details.title) : undefined;

  // Without a building there is nothing to look up; still render the initials, which are the more
  // informative half of a visitor tile anyway.
  if (!context.buildingUuid) {
    return <EntityIcon size={SIZE} text={text} fallback={fallback} alt="" />;
  }

  return <BuildingEntityIcon size={SIZE} buildingUuid={context.buildingUuid} text={text} fallback={fallback} alt="" />;
};
